import { defineStore } from 'pinia'
import { computed } from 'vue'
import { createIndexedDBStore } from '@/stores/_base/createIndexedDBStore'
import { defineService, SERVICE_DEFINITIONS } from '@/services/_base/defineService'
import type { PaymentMatchGroup } from '@farmfree/common/types/payment/paymentMatchGroup'
import { Timestamp } from 'firebase/firestore'

// 공통 서비스 팩토리 사용
const createPaymentMatchGroupService = defineService<PaymentMatchGroup>(
  SERVICE_DEFINITIONS.paymentMatchGroup
)

// 기본 Store 팩토리 생성 (createIndexedDBStore 패턴)
const basePaymentMatchStore = createIndexedDBStore<PaymentMatchGroup>({
  storeId: 'paymentMatch',
  tableName: 'payment-match-groups',
  versionField: 'paymentMatchGroupVersion',
  service: createPaymentMatchGroupService('admin'),
})

/**
 * PaymentMatchGroup Store
 *
 * 매칭 정보를 메모리에서 관리하고 O(1) 조회 제공
 * - createIndexedDBStore 패턴 사용
 * - handleCompanyChange에서 initializeCache 호출로 초기화
 * - 매칭 여부 확인을 위한 Map 제공
 */
export const usePaymentMatchStore = defineStore('paymentMatchExtended', () => {
  const store = basePaymentMatchStore()

  // ==================== Computed (O(1) 조회용 Map) ====================

  /**
   * 주문 ID → 매칭 그룹 Map
   * 주문이 매칭되었는지 O(1)로 확인 가능
   */
  const matchGroupsByOrderId = computed(() => {
    const map = new Map<string, PaymentMatchGroup>()
    for (const group of store.items) {
      for (const orderId of group.orderIds) {
        map.set(orderId, group)
      }
    }
    return map
  })

  /**
   * 거래내역 ID → 매칭 그룹 Map
   * 거래내역이 매칭되었는지 O(1)로 확인 가능
   */
  const matchGroupsByTransactionId = computed(() => {
    const map = new Map<string, PaymentMatchGroup>()
    for (const group of store.items) {
      for (const txId of group.transactionIds) {
        map.set(txId, group)
      }
    }
    return map
  })

  /**
   * 총 매칭 그룹 수
   */
  const groupCount = computed(() => store.items.length)

  // ==================== 조회 헬퍼 ====================

  /**
   * 주문이 매칭되었는지 확인
   */
  function isOrderMatched(orderId: string): boolean {
    return matchGroupsByOrderId.value.has(orderId)
  }

  /**
   * 거래내역이 매칭되었는지 확인
   */
  function isTransactionMatched(transactionId: string): boolean {
    return matchGroupsByTransactionId.value.has(transactionId)
  }

  /**
   * 주문에 매칭된 거래내역 ID들 반환
   */
  function getMatchedTransactionIds(orderId: string): string[] {
    const group = matchGroupsByOrderId.value.get(orderId)
    return group?.transactionIds || []
  }

  /**
   * 거래내역에 매칭된 주문 ID들 반환
   */
  function getMatchedOrderIds(transactionId: string): string[] {
    const group = matchGroupsByTransactionId.value.get(transactionId)
    return group?.orderIds || []
  }

  // ==================== 비즈니스 액션 ====================

  /**
   * 매칭 그룹 생성
   * 클라이언트에서 금액을 계산하여 전달 (서버 읽기 비용 절감)
   */
  async function createGroup(
    companyId: string,
    orderIds: string[],
    transactionIds: string[],
    userId: string,
    totalOrderAmount: number,
    totalTransactionAmount: number,
    memo?: string
  ): Promise<{ success: boolean; groupId?: string; message: string }> {
    try {
      const now = Timestamp.fromDate(new Date())

      const group: PaymentMatchGroup = {
        id: '', // saveItem에서 생성
        orderIds,
        transactionIds,
        totalOrderAmount,
        totalTransactionAmount,
        matchType: 'manual',
        matchedBy: userId,
        ...(memo && { memo }),
        dateCreated: now,
        dateModified: now,
      }

      const result = await store.saveItem(companyId, group)

      if (result.isSuccess && result.data?.id) {
        // saveItem이 낙관적 업데이트 + 서버 저장을 수행하므로 추가 동기화 불필요
        console.log('🔍 [PaymentMatchStore] 저장 완료 - items:', store.items.length)
        console.log(
          '🔍 [PaymentMatchStore] matchGroupsByOrderId 크기:',
          matchGroupsByOrderId.value.size
        )

        return {
          success: true,
          groupId: result.data.id,
          message: result.message || '매칭 그룹이 생성되었습니다.',
        }
      }

      return {
        success: false,
        message: result.message || '매칭 생성 실패',
      }
    } catch (err: any) {
      console.error('[PaymentMatchStore] 매칭 생성 실패:', err)
      return {
        success: false,
        message: err.message || '매칭 생성 실패',
      }
    }
  }

  /**
   * 주문 매칭 해제 (주문이 속한 그룹 전체 삭제)
   */
  async function unmatchOrder(
    companyId: string,
    orderId: string
  ): Promise<{ success: boolean; message: string }> {
    const group = matchGroupsByOrderId.value.get(orderId)

    if (!group) {
      return {
        success: false,
        message: '매칭 정보를 찾을 수 없습니다.',
      }
    }

    const result = await store.deleteItem(companyId, group.id)
    return {
      success: result.isSuccess,
      message: result.message || (result.isSuccess ? '매칭이 해제되었습니다.' : '매칭 해제 실패'),
    }
  }

  /**
   * 거래내역 매칭 해제 (거래내역이 속한 그룹 전체 삭제)
   */
  async function unmatchTransaction(
    companyId: string,
    transactionId: string
  ): Promise<{ success: boolean; message: string }> {
    const group = matchGroupsByTransactionId.value.get(transactionId)

    if (!group) {
      return {
        success: false,
        message: '매칭 정보를 찾을 수 없습니다.',
      }
    }

    const result = await store.deleteItem(companyId, group.id)
    return {
      success: result.isSuccess,
      message: result.message || (result.isSuccess ? '매칭이 해제되었습니다.' : '매칭 해제 실패'),
    }
  }

  // ==================== Return ====================
  return {
    // Base Store (createIndexedDBStore)
    ...store,

    // Computed (O(1) 조회용)
    matchGroupsByOrderId,
    matchGroupsByTransactionId,
    groupCount,

    // 조회 헬퍼
    isOrderMatched,
    isTransactionMatched,
    getMatchedTransactionIds,
    getMatchedOrderIds,

    // 비즈니스 액션
    createGroup,
    unmatchOrder,
    unmatchTransaction,
  }
})
